import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCalendar, faClock, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { bookingsWriteApi } from '@/api/api';
import { useToast } from '@/hooks/useToast';

interface RescheduleBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  currentDate: string;
  currentTime: string;
  currentStaffName: string;
  staffList: Array<{ id: string; name: string }>;
  onSuccess?: () => void;
}

const RescheduleBookingModal: React.FC<RescheduleBookingModalProps> = ({
  isOpen,
  onClose,
  bookingId,
  currentDate,
  currentTime,
  currentStaffName,
  staffList,
  onSuccess,
}) => {
  const [newDate, setNewDate] = useState(currentDate);
  const [newTime, setNewTime] = useState(currentTime);
  const [newStaffId, setNewStaffId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewDate(currentDate);
      setNewTime(currentTime);
      setNewStaffId('');
      setNotes('');
    }
  }, [isOpen, currentDate, currentTime]);

  // Generujeme časové sloty (8:00 - 20:00 po 30 minutách)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newDate || !newTime) {
      toast.error('Chyba!', 'Vyplňte prosím datum a čas');
      return;
    }

    setIsSubmitting(true);

    try {
      const moveRequest = {
        newDate,
        newStartTime: newTime,
        newWorkerId: newStaffId || undefined,
        notes: notes || `Přesunuto z ${dayjs(currentDate).format('DD.MM.YYYY')} ${currentTime}`,
      };

      await bookingsWriteApi.moveBooking({
        bookingId,
        moveBookingRequest: moveRequest,
      });

      toast.success('Úspěch!', 'Rezervace byla úspěšně přeplánována');
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Chyba při přeplánování rezervace:', error);
      toast.error('Chyba!', 'Nepodařilo se přeplánovat rezervaci');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const hasChanges = newDate !== currentDate || newTime !== currentTime || newStaffId !== '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCalendar} className="text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Přeplánovat rezervaci</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Current Info */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Současný termín:</h3>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faCalendar} className="mr-2" />
              {dayjs(currentDate).format('D. MMMM YYYY')}
            </div>
            <div className="flex items-center">
              <FontAwesomeIcon icon={faClock} className="mr-2" />
              {currentTime}
            </div>
            <div>
              <strong>{currentStaffName}</strong>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* New Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nové datum *</label>
            <input
              type="date"
              value={newDate}
              onChange={e => setNewDate(e.target.value)}
              min={dayjs().format('YYYY-MM-DD')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* New Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nový čas *</label>
            <select
              value={newTime}
              onChange={e => setNewTime(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Vyberte čas</option>
              {timeSlots.map(time => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>

          {/* New Staff (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Změnit zaměstnance (volitelné)
            </label>
            <select
              value={newStaffId}
              onChange={e => setNewStaffId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Ponechat {currentStaffName}</option>
              {staffList.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Poznámka</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Důvod přeplánování..."
            />
          </div>

          {/* Change indicator */}
          {hasChanges && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center text-blue-800 text-sm">
                <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                <span>
                  Rezervace bude přesunuta na {dayjs(newDate).format('D.M.YYYY')} v {newTime}
                  {newStaffId && ` (${staffList.find(s => s.id === newStaffId)?.name})`}
                </span>
              </div>
            </div>
          )}

          {!hasChanges && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center text-yellow-800 text-sm">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                <span>Změňte datum, čas nebo zaměstnance</span>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !hasChanges}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Přeplánování...
                </>
              ) : (
                'Přeplánovat rezervaci'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RescheduleBookingModal;

